/**
 * Feb 2026 R293zr · Document attachments panel.
 *
 * Upload PDFs / images / docs to a saved gst_documents row so the
 * full brief travels with the document. Used by both the Composer
 * (edit mode, upload + delete) and the DetailDialog (view + download).
 *
 * Props:
 *   ws         workspace id
 *   docId      gst_documents.id (required). Panel is hidden until the
 *              doc has been saved at least once.
 *   readOnly   hide upload + delete affordances (default false)
 *   initial    seed attachments from the parent (first paint); the
 *              panel refetches on mount anyway.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Paperclip, Upload, Download, Trash2, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon, Loader2 } from "lucide-react";

const ALLOWED_MIMES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg", "image/png", "image/webp", "image/gif", "image/heic",
  "text/plain", "text/csv",
];
const ACCEPT_ATTR = ALLOWED_MIMES.join(",");
const MAX_MB = 15;

function iconFor(ctype = "") {
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype === "application/pdf") return FileText;
  if (ctype.includes("spreadsheet") || ctype.includes("excel") || ctype === "text/csv") return FileSpreadsheet;
  return FileIcon;
}
function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function AttachmentsPanel({ ws, docId, basePath, readOnly = false, initial = [] }) {
  const [items, setItems] = useState(initial || []);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Feb 2026 R293zv · Accept an explicit `basePath` for callers outside
  // the Composer (e.g. Item Catalog). Falls back to the gst doc path
  // for existing callers that still pass `ws` + `docId`.
  const base = basePath || (ws && docId ? `/gst/workspaces/${ws}/documents/${docId}/attachments` : null);

  const refresh = useCallback(async () => {
    if (!base) return;
    setLoading(true);
    try {
      const r = await api.get(base);
      setItems(r.data.attachments || []);
    } catch (e) {
      // silent; empty list is a valid state
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { refresh(); }, [refresh]);

  const onPickFile = () => fileRef.current?.click();

  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file || !base) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit.`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(base, form, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [...prev, r.data]);
      toast.success(`Attached ${r.data.filename}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }, [base]);

  const onDownload = useCallback(async (att) => {
    if (!base) return;
    setBusyId(att.id);
    try {
      const r = await api.get(`${base}/${att.id}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: att.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || att.id;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }, [base]);

  const onDelete = useCallback(async (att) => {
    if (!base) return;
    if (!window.confirm(`Remove "${att.filename}" from this document?`)) return;
    setBusyId(att.id);
    try {
      await api.delete(`${base}/${att.id}`);
      setItems((prev) => prev.filter((x) => x.id !== att.id));
      toast.success("Attachment removed");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }, [base]);

  if (!base) {
    // Composer hasn't saved yet — show a hint so the user knows attachments
    // open up after the first save.
    return (
      <div className="border border-dashed border-border rounded p-3 text-xs text-muted-foreground"
           data-testid="gst-attachments-locked">
        <Paperclip className="h-3.5 w-3.5 inline mr-1 opacity-60" />
        Save the document first to attach PDFs, images or spec sheets.
      </div>
    );
  }

  return (
    <div className="border border-border rounded p-3 bg-muted/10 space-y-2"
         data-testid="gst-attachments-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5" />
          Attachments
          {items.length > 0 && (
            <span className="font-mono tabular-nums text-[10px] bg-background border border-border rounded px-1.5"
                  data-testid="gst-attachments-count">
              {items.length}
            </span>
          )}
        </div>
        {!readOnly && (
          <>
            <input
              type="file"
              ref={fileRef}
              onChange={onFile}
              accept={ACCEPT_ATTR}
              className="hidden"
              data-testid="gst-attachments-file-input"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={onPickFile}
              disabled={uploading}
              data-testid="gst-attachments-upload-btn"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              {uploading ? "Uploading…" : "Attach file"}
            </Button>
          </>
        )}
      </div>
      {loading && items.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">Loading attachments…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2"
             data-testid="gst-attachments-empty">
          No attachments yet. {!readOnly && <>Up to {MAX_MB} MB per file.</>}
        </div>
      ) : (
        <ul className="divide-y divide-border rounded border border-border overflow-hidden bg-background">
          {items.map((att) => {
            const Icon = iconFor(att.content_type);
            return (
              <li
                key={att.id}
                className="flex items-center gap-2 px-2 py-1.5 text-xs"
                data-testid={`gst-attachment-row-${att.id}`}
              >
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium" title={att.filename}>{att.filename}</div>
                  <div className="text-muted-foreground font-mono text-[10px]">
                    {fmtSize(att.size)}
                    {att.uploaded_by_name && <> · {att.uploaded_by_name}</>}
                    {att.uploaded_at && <> · {String(att.uploaded_at).slice(0, 16).replace("T", " ")}</>}
                  </div>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => onDownload(att)}
                  disabled={busyId === att.id}
                  title="Download"
                  data-testid={`gst-attachment-download-${att.id}`}
                >
                  {busyId === att.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                </Button>
                {!readOnly && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-rose-600 hover:text-rose-700"
                    onClick={() => onDelete(att)}
                    disabled={busyId === att.id}
                    title="Remove attachment"
                    data-testid={`gst-attachment-delete-${att.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
